import { useState } from "react";
import { toast } from "react-toastify";
import { Siren, Phone, MessageCircle, Send, Check, CheckCircle2 } from "lucide-react";
import api from "../../api";

// One open crisis alert on the patient record, for whoever is handling it.
// Laid out as the steps of handling it: take it ("I'm on it"), reach the
// patient, then close it with what was done. There is one message box, and
// both message buttons send exactly what's in it (in the app, or opened in
// WhatsApp), so every button behaves the same way. The closing note is a
// separate, labelled step so it's never mistaken for a message.

const waNumber = (phone) => {
  const d = (phone || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};
const timeNow = () => new Date().toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });

function Step({ n, title, done, children }) {
  return (
    <section className="flex gap-3">
      <span className={`mt-0.5 w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`}>
        {done ? <Check className="w-3.5 h-3.5" /> : n}
      </span>
      <div className="flex-1 min-w-0 space-y-2.5">
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        {children}
      </div>
    </section>
  );
}

export default function CrisisAlertCard({ alert: a, me, canClose, raisedLabel, fmtDateTime, onAcknowledge, onResolve }) {
  const [message, setMessage] = useState(a.contact_message || "");
  const [sending, setSending] = useState(false);
  const [contacted, setContacted] = useState([]); // what was done from this card, for the closing note
  const [note, setNote] = useState("");
  const [closing, setClosing] = useState(false);
  const open = a.status === "open";
  const firstName = (a.patient_name || "").split(" ")[0] || "the patient";
  const canMessage = a.can_message || a.patient_phone;
  const logContact = (text) => setContacted((c) => (c.includes(text) ? c : [...c, text]));

  const sendInApp = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await api.messageCrisisPatient(a.id, message);
      toast.success("Sent to the patient's app");
      logContact(`Messaged in the app at ${timeNow()}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't send the message. Please call instead.");
    } finally {
      setSending(false);
    }
  };

  const close = async () => {
    if (!note.trim()) return;
    setClosing(true);
    try { await onResolve(a, note); } finally { setClosing(false); }
  };

  return (
    <div className="rounded-2xl border border-rose-300 bg-white shadow-sm overflow-hidden">
      {/* What happened */}
      <div className="bg-rose-50 border-b border-rose-200 px-4 py-3 md:px-5 space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Siren className="w-5 h-5 text-rose-600 shrink-0" />
          <h2 className="font-bold text-rose-900">{raisedLabel}</h2>
          <span className="text-sm text-rose-700">{fmtDateTime(a.created_at)}</span>
          <span className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-bold ${open ? "bg-rose-600 text-white" : "bg-amber-100 text-amber-900"}`}>
            {open ? "Nobody on it yet" : a.acknowledged_by === me ? "You're handling this" : `${a.acknowledged_by_name || "A colleague"} is handling this`}
          </span>
        </div>
        <p className="text-sm text-rose-950">
          {a.summary || (a.raised_by_patient ? "The patient asked for help from their app." : "No message was added. Call the person who raised it.")}
        </p>
      </div>

      <div className="px-4 py-4 md:px-5 space-y-5">
        {open && (
          <Step n={1} title="Take this alert">
            <p className="text-sm text-gray-600">So the rest of the team knows someone is on it.</p>
            <span className="relative inline-flex">
              <span className="absolute inset-0 rounded-xl bg-rose-500 opacity-50 animate-ping" aria-hidden="true" />
              <button type="button" onClick={() => onAcknowledge(a)}
                className="relative inline-flex items-center gap-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-extrabold px-5 py-2.5 shadow-lg shadow-rose-600/30">
                <Siren className="w-5 h-5" /> I'm on it
              </button>
            </span>
          </Step>
        )}

        <Step n={open ? 2 : 1} title={`Reach ${firstName}`} done={contacted.length > 0}>
          {a.patient_phone && (
            <a href={`tel:${a.patient_phone}`} onClick={() => logContact(`Called at ${timeNow()}`)}
              className="inline-flex items-center gap-2 rounded-xl bg-gray-900 hover:bg-black text-white font-bold px-4 py-2.5">
              <Phone className="w-4 h-4" /> Call {a.patient_phone}
            </a>
          )}
          {canMessage && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
              <label htmlFor={`crisis-msg-${a.id}`} className="block normal-case font-sans tracking-normal text-sm font-semibold text-gray-800">
                {a.patient_phone ? "Or send a message" : "Send a message"}
                <span className="font-normal text-gray-500"> - edit it if you like, then choose how to send it</span>
              </label>
              <textarea id={`crisis-msg-${a.id}`} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000}
                className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 min-h-[84px] focus:ring-2 focus:ring-rose-400" />
              <div className="flex flex-wrap gap-2">
                {a.can_message && (
                  <button type="button" onClick={sendInApp} disabled={sending || !message.trim()}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold px-3.5 py-2 disabled:opacity-50">
                    <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send in the app"}
                  </button>
                )}
                {a.patient_phone && (
                  <a href={`https://wa.me/${waNumber(a.patient_phone)}?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer"
                    onClick={() => logContact(`Sent a WhatsApp at ${timeNow()}`)}
                    className={`inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-3.5 py-2 ${message.trim() ? "" : "pointer-events-none opacity-50"}`}>
                    <MessageCircle className="w-4 h-4" /> Send on WhatsApp
                  </a>
                )}
              </div>
              {a.can_message && <p className="text-xs text-gray-500">"Send in the app" reaches their booking app with the usual new-message alert. WhatsApp opens with this text ready to send.</p>}
            </div>
          )}
          {contacted.length > 0 && (
            <ul className="space-y-1">
              {contacted.map((c) => (
                <li key={c} className="flex items-center gap-1.5 text-sm text-emerald-800"><CheckCircle2 className="w-4 h-4" /> {c}</li>
              ))}
            </ul>
          )}
        </Step>

        {canClose && !open && (
          <div className="border-t border-gray-200 pt-4">
            <Step n={2} title="Close the alert when it's handled">
              <label htmlFor={`crisis-note-${a.id}`} className="block normal-case font-sans tracking-normal text-sm text-gray-700">
                What was done? <span className="text-gray-500">This goes on the patient's record; the patient doesn't see it.</span>
              </label>
              {contacted.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {contacted.map((c) => (
                    <button key={c} type="button" onClick={() => setNote((n) => (n.trim() ? `${n.trim()} ${c}.` : `${c}.`))}
                      className="rounded-full border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:border-rose-400">+ {c}</button>
                  ))}
                </div>
              )}
              <textarea id={`crisis-note-${a.id}`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
                placeholder="e.g. Spoke to them at 3:20 pm. Safe at home with family, agreed the safety plan, session booked for tomorrow."
                className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 min-h-[72px] focus:ring-2 focus:ring-rose-400" />
              <button type="button" onClick={close} disabled={!note.trim() || closing}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 text-sm font-bold px-3.5 py-2 disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" /> {closing ? "Closing…" : "Close alert"}
              </button>
            </Step>
          </div>
        )}
        {!open && !canClose && (
          <p className="text-sm text-gray-600 border-t border-gray-200 pt-3">{a.acknowledged_by_name || "A colleague"} is handling this and will close it.</p>
        )}
      </div>
    </div>
  );
}
